import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import { useT } from "../../../core/i18n";
import Api from "../../../core/data/redux/utils/apiCall";
import {
  ID_CARD_STATUS_KEYS,
  findAllIdCards,
  formatSapDate,
  getLoggedInEmpId,
  getStatusMeta,
  isDraftStatus,
  padEmpId,
  syncIdCard,
} from "./idCard.service";

// Exact checklist copy from the mobile app (StatusICARD.tsx) — do not shorten.
const ICARD_CHECKLIST = [
  {
    english: "I] Blood group",
    hindi: "I] ब्लड ग्रुप",
    items: ["Enter blood group (+ve/-ve).", "ब्लड ग्रुप (+VE/-VE) प्रविष्ट करें।"],
  },
  {
    english: "II] Contact no.",
    hindi: "II] कॉन्टेक्ट नंबर",
    items: ["Enter 10-digit mobile number.", "10 अंकों का मोबाइल नंबर प्रविष्ट करें।"],
  },
  {
    english: "III] Application applied for the reason",
    hindi: "III] आवेदन करने का उद्देश्य",
    items: [
      "Choose correct reason and upload supporting documents accordingly:",
      "सही कारण चुनें और तदनुसार अपेक्षित दस्तावेज़ अपलोड करें:",
      "- Issue of fresh I-card on joining - Upload complete joining office order using the hyperlink provided.",
      "- ज्वाइनिंग पर नया पहचान पत्र जारी करना - दिए गए हाइपरलिंक का उपयोग करके ज्वाइनिंग कार्यालय आदेश पूर्ण रूप में अपलोड करें।",
      "- After expiry of I-card - Upload image of old I-card. In case of extension of contractual service upload officer order for the same.",
      "- पहचान पत्र की समाप्ति के बाद - पुराने पहचान पत्र की फोटो अपलोड करें। संविदात्मक सेवा के विस्तार के मामले में कार्यालय आदेश अपलोड करें।",
      "- Change in Designation or Promotion - Upload complete promotion office order using the hyperlink provided. Ensure that the new designation is updated in SAP.",
      "- पदनाम में परिवर्तन या पदोन्नति - दिए गए हाइपरलिंक का उपयोग करके पदोन्नति कार्यालय आदेश पूर्ण रूप में अपलोड करें। सुनिश्चित करें कि नया पदनाम एसएपी में अद्यतन हो।",
      "- Loss of identity card - Upload image of FIR clearly mentioning loss of DMRC identity card.",
      "- पहचान पत्र का गुम होना - एफआईआर की कॉपी अपलोड करें जिसमें डीएमआरसी के पहचान पत्र खो जाने का स्पष्ट रूप से उल्लेख हो।",
      "- Damage of I-card - Upload image of damaged I-card.",
      "- पहचान पत्र का क्षतिग्रस्त होना - क्षतिग्रस्त पहचान पत्र की फोटो अपलोड करें।",
      "- Correction in I-card - Upload image of old I-card and fill the remarks field for explaining the correction.",
      "- पहचान पत्र में सुधार - पुराने पहचान पत्र की कॉपी अपलोड करें और क्या सुधार होना है, उसके लिए टिप्पणी फील्ड में जानकारी दें।",
    ],
  },
  {
    english:
      "IV] Upload passport size studio quality latest/recent photo - (jpg or jpeg file type only and file size 20-50Kb) - passport size studio quality formal photo with clear background. Kindly do not upload selfie.",
    hindi:
      "IV] पासपोर्ट आकार की स्टूडियो क्वालिटी की नवीनतम फोटो अपलोड करें - (केवल JPG या JPEG फाइल 20 से 50 KB साइज़ में) - स्पष्ट पृष्ठभूमि के साथ औपचारिक फोटो। कृपया सेल्फी अपलोड न करें।",
    items: [] as string[],
  },
  {
    english:
      "V] Upload sign (jpg or jpeg file type only and file size 10-30Kb) - signature should be on blank white page. Do not write your designation, employee id, date etc. with signature.",
    hindi:
      "V] हस्ताक्षर अपलोड करें (केवल JPG या JPEG फाइल 10 से 30 KB साइज़ में) - हस्ताक्षर खाली सफेद पृष्ठ पर होना चाहिए। हस्ताक्षर के साथ अपना पदनाम, कर्मचारी आईडी, दिनांक आदि न लिखें।",
    items: [] as string[],
  },
  {
    english:
      "VI] If application is stuck in the channel before reaching HR department there might be an error in controlling officer hierarchy. In such cases, kindly get the same corrected/updated in SAP.",
    hindi:
      "VI] यदि आवेदन मानव संसाधन विभाग तक पहुंचने से पहले किसी चैनल में रुका हो तो नियंत्रण अधिकारी के पदानुक्रम में त्रुटि हो सकती है। ऐसे मामलों में कृपया इसे SAP में ठीक/अपडेट करा लें।",
    items: [] as string[],
  },
  {
    english:
      "VII] Forwarding authority\n- For non-executive  - concerned DY. HOD\n- For AM/MGR/D.HOD  - concerned HOD\n- HOD and above  - self",
    hindi:
      "VII] अग्रेषित करने वाले प्राधिकारी\n- गैर-कार्यकारी के लिए - संबंधित उप विभागाध्यक्ष\n- सहायक प्रबंधक/प्रबंधक/उप विभागाध्यक्ष के लिए - संबंधित विभागाध्यक्ष\n- विभागाध्यक्ष एवं उससे ऊपर के अधिकारी - स्वयं",
    items: [] as string[],
  },
  {
    english:
      "Note: It takes 10 days to get I-card printed after the application is approved by HR department. Therefore, employees are requested to collect their I-card after 10 days of their application status showing 'Processing Complete' in ESS.",
    hindi:
      'टिप्पणी: मानव संसाधन विभाग द्वारा आवेदन स्वीकृत होने के बाद पहचान पत्र प्रिंट होने में 10 दिन लगते हैं। इसलिए, कर्मचारियों से अनुरोध है कि वे ESS में "Processing Complete" दिखने के 10 दिनों बाद अपना पहचान पत्र प्राप्त करें।',
    items: [] as string[],
  },
];

const ChecklistModal: React.FC<{ onClose: () => void; onProceed: () => void }> = ({
  onClose,
  onProceed,
}) => (
  <>
    <div
      className="position-fixed top-0 start-0 w-100 h-100"
      style={{ zIndex: 1060, background: "rgba(0,0,0,0.55)" }}
      onClick={onClose}
    />
    <div
      className="position-fixed top-50 start-50 translate-middle bg-white shadow-lg overflow-hidden"
      style={{
        zIndex: 1070,
        width: "92%",
        maxWidth: "640px",
        maxHeight: "88vh",
        display: "flex",
        flexDirection: "column",
        borderRadius: "15px",
      }}
    >
      <div className="d-flex align-items-start justify-content-between px-4 pt-4 pb-2">
        <h5 className="mb-0 fw-bold pe-4" style={{ fontSize: "16px", lineHeight: 1.45 }}>
          Checklist: - Application for new I-card / चेकलिस्ट - नए पहचान पत्र के लिए आवेदन
        </h5>
        <button
          type="button"
          className="btn btn-link text-danger text-decoration-none p-0 flex-shrink-0"
          onClick={onClose}
          aria-label="Close checklist modal"
        >
          <i className="ti ti-x fs-4" />
        </button>
      </div>
      <div className="px-4 pb-2 overflow-auto" style={{ lineHeight: 1.55 }}>
        {ICARD_CHECKLIST.map((section) => (
          <div key={section.english} className="mb-3">
            <div className="fw-bold text-dark mb-1" style={{ whiteSpace: "pre-line", fontSize: "14px" }}>
              {section.english}
            </div>
            <div className="text-dark mb-1" style={{ whiteSpace: "pre-line", fontSize: "14px" }}>
              {section.hindi}
            </div>
            {section.items.map((item) => (
              <div
                key={item}
                className="text-dark mb-1"
                style={{ marginLeft: "16px", fontSize: "14px", whiteSpace: "pre-line" }}
              >
                {item}
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="px-4 py-3 text-center">
        <button
          type="button"
          className="btn btn-danger fw-bold px-5"
          style={{ borderRadius: "25px", minWidth: "140px" }}
          onClick={onProceed}
        >
          OK
        </button>
      </div>
    </div>
  </>
);

const InfoRow: React.FC<{ label: string; value?: any }> = ({ label, value }) => (
  <div className="d-flex justify-content-between gap-3 py-1">
    <span className="text-muted small">{label}</span>
    <strong className="text-dark text-end small">{value || "-"}</strong>
  </div>
);

const IDCardList: React.FC = () => {
  const { t } = useT();
  const routes = all_routes;
  const navigate = useNavigate();

  const [cards, setCards] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showChecklist, setShowChecklist] = useState(false);
  const filtersRef = useRef({ startDate, endDate, statusFilter, page });
  filtersRef.current = { startDate, endDate, statusFilter, page };

  const fetchCards = useCallback(
    async (targetPage: number) => {
      const empId = getLoggedInEmpId();
      if (!empId) {
        toast.error(t("employee_id_not_found") || "Employee ID not found");
        return;
      }
      const { startDate: sd, endDate: ed, statusFilter: sf } = filtersRef.current;
      setLoading(true);
      try {
        const result = await findAllIdCards({
          page: targetPage,
          limit: 10,
          startDate: sd,
          endDate: ed,
          status: sf,
          empId,
        });
        setCards((prev) => (targetPage === 1 ? result.list : [...prev, ...result.list]));
        setTotalPages(result.totalPages || 1);
        setPage(targetPage);
      } catch (err: any) {
        if (targetPage === 1) setCards([]);
        toast.error(Api.getErrorMessage(err) || t("failed_to_load_details") || "Failed to load ID card requests.");
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    fetchCards(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, statusFilter]);

  const handleSync = async () => {
    const empId = padEmpId(getLoggedInEmpId());
    if (!empId) {
      toast.error(t("employee_id_not_found") || "Employee ID not found");
      return;
    }
    setSyncing(true);
    try {
      await syncIdCard(empId);
      toast.success(t("synced_successfully") || "Data synced successfully.");
      await fetchCards(1);
    } catch (err: any) {
      toast.error(Api.getErrorMessage(err) || "Sync failed. Please try again.");
    } finally {
      setSyncing(false);
    }
  };

  const statusLabel = (key: string) => {
    if (!key) return t("all") || "All";
    const meta = getStatusMeta(key);
    const translated = t(meta.labelKey);
    return translated && translated !== meta.labelKey ? translated : meta.label;
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">{t("Application_for_new_I-Card")}</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.employeeDashboard}>{t("home") || "Home"}</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.services.list}>{t("services") || "Services"}</Link>
                </li>
                <li className="breadcrumb-item active">{t("see_previous_request")}</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={handleSync}
              disabled={syncing || loading}
            >
              <i className={`ti ti-refresh me-1 ${syncing ? "spin" : ""}`} />
              {syncing ? t("syncing") || "Syncing…" : t("sync") || "Sync"}
            </button>
            <button type="button" className="btn btn-danger" onClick={() => setShowChecklist(true)}>
              <i className="ti ti-plus me-1" />
              {t("create_new_request")}
            </button>
          </div>
        </div>

        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-md-3">
                <label className="form-label fw-semibold small">{t("start_date") || "Start Date"}</label>
                <input
                  type="date"
                  className="form-control"
                  value={startDate}
                  max={endDate || new Date().toISOString().slice(0, 10)}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (endDate && value > endDate) {
                      toast.error("Start date cannot be after end date.");
                      return;
                    }
                    setStartDate(value);
                  }}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold small">{t("end_date") || "End Date"}</label>
                <input
                  type="date"
                  className="form-control"
                  value={endDate}
                  min={startDate || undefined}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (startDate && value < startDate) {
                      toast.error("End date cannot be before start date.");
                      return;
                    }
                    setEndDate(value);
                  }}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold small">{t("filter_by_status") || "Status"}</label>
                <select
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  {ID_CARD_STATUS_KEYS.map((key) => (
                    <option key={key || "all"} value={key}>
                      {statusLabel(key)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-3 d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-danger flex-fill"
                  onClick={() => fetchCards(1)}
                  disabled={loading}
                >
                  {t("refresh_list") || "Refresh"}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setStartDate("");
                    setEndDate("");
                    setStatusFilter("");
                  }}
                >
                  {t("clear") || "Clear"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {loading && page === 1 ? (
          <div className="text-center py-5">
            <div className="spinner-border text-danger" role="status" />
          </div>
        ) : cards.length === 0 ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5 text-muted">
              {t("no_data_available_for_the_selected_filters") || t("no_records_found") || "No requests found."}
            </div>
          </div>
        ) : (
          <>
            <div className="row g-3">
              {cards.map((card: any, idx: number) => {
                const status = card.RequestStatus || card.requestStatus || card.status || "";
                const meta = getStatusMeta(status);
                const translated = t(meta.labelKey);
                const label = translated && translated !== meta.labelKey ? translated : meta.label;
                const refNo = card.ReferenceNumber || card.referenceNumber || "-";
                const draft = isDraftStatus(status);
                const navId = String(refNo !== "-" ? refNo : card.id || idx);
                return (
                  <div className="col-xl-6" key={card.id || refNo || idx}>
                    <div className="card border shadow-sm h-100">
                      <div className="card-header bg-white d-flex justify-content-between align-items-center gap-2">
                        <span className="fw-bold">
                          {t("ref_no.") || t("reference_no") || "Ref No."}: {refNo}
                        </span>
                        <span className={`badge rounded-pill ${meta.cls}`}>{label}</span>
                      </div>
                      <div className="card-body">
                        <InfoRow
                          label={t("request_creation_date") || "Request Creation Date"}
                          value={formatSapDate(card.RequestCreationDate || card.requestCreationDate)}
                        />
                        <InfoRow label={t("blood_group")} value={card.BloodGroup || card.bloodGroup} />
                        <InfoRow
                          label={t("contact_number") || t("contact_no") || "Contact Number"}
                          value={card.ContactNumber || card.contactNumber}
                        />
                        <InfoRow
                          label={t("place_of_posting") || t("Place_of_Posting")}
                          value={card.PlaceOfPosting || card.placeOfPosting}
                        />
                        <InfoRow
                          label={t("Last_Latest_Approved") || t("last_latest_approved")}
                          value={card.ApproverName || "-"}
                        />
                        <InfoRow label={t("ApproverUserID") || "Approver User ID"} value={card.ApproverUserID || "-"} />
                        <div className="d-flex gap-2 mt-3">
                          {draft ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              onClick={() => navigate(routes.idCard.formEdit.replace(":id", navId))}
                            >
                              {t("edit_and_submit") || "Edit & Submit"}
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => navigate(routes.idCard.details.replace(":id", navId))}
                            >
                              {t("view_details") || "View Details"}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {page < totalPages && (
              <div className="text-center mt-3">
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={() => fetchCards(page + 1)}
                  disabled={loading}
                >
                  {loading ? (
                    <span className="spinner-border spinner-border-sm me-1" />
                  ) : (
                    <i className="ti ti-chevrons-down me-1" />
                  )}
                  {t("load_more") || "Load More"}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {showChecklist && (
        <ChecklistModal
          onClose={() => setShowChecklist(false)}
          onProceed={() => {
            setShowChecklist(false);
            navigate(routes.idCard.form);
          }}
        />
      )}
    </div>
  );
};

export default IDCardList;
